import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout, getParentLayout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'Redirect',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect2',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      // title: t('router.login'),
      title: '登录',
      noTagsView: true,
      icon: 'tdesign:user-setting'
    }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      // title: t('router.personal'),
      title: '个人',
      hidden: true,
      canTo: true,
      icon: 'tdesign:user-setting'
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/index.vue'),
        name: 'PersonalCenter',
        meta: {
          // title: t('router.personalCenter'),
          title: '个人中心',
          hidden: true,
          canTo: true,
          icon: 'tdesign:user-setting'
        }
      }
    ]
  },
  {
    path: '/notice',
    component: Layout,
    redirect: '/notice/notice-center',
    name: 'Notice',
    meta: {
      title: '公告',
      hidden: true,
      canTo: true,
      icon: 'ep:bell-filled'
    },
    children: [
      {
        path: 'notice-center',
        component: () => import('@/views/Notice/NoticeCenter/index.vue'),
        name: 'NoticeCenter',
        meta: {
          title: '公告列表',
          hidden: true,
          canTo: true,
          icon: 'ep:bell-filled'
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/dashboard/analysis',
    name: 'Dashboard',
    meta: {
      title: t('router.dashboard'),
      icon: 'tdesign:user-setting'
    },
    children: [
      {
        path: 'analysis',
        component: () => import('@/views/Dashboard/Analysis.vue'),
        name: 'Analysis',
        meta: {
          title: t('router.dashboard'),
          icon: 'tdesign:user-setting',
          noCache: true,
          affix: true
        }
      },
      {
        path: 'approval',
        name: 'Approval',
        component: () => import('@/views/MyApproval/index.vue'),
        meta: {
          title: t('router.myApproval'),
          icon: 'svg-icon:approval'
        }
      }
    ]
  },
  {
    path: '/resourceManagement',
    component: Layout,
    redirect: '/resourceManagement/instrumentsAndEquipment/instrumentEquipmentLedger',
    name: 'ResourceManagement',
    meta: {
      title: t('router.resourceManagement'),
      icon: 'svg-icon:resource-management'
    },
    children: [
      {
        path: 'instrumentsAndEquipment',
        name: 'InstrumentsAndEquipment',
        component: getParentLayout(),
        redirect: '/resourceManagement/instrumentsAndEquipment/instrumentEquipmentLedger',
        meta: {
          title: t('analysis.instrumentsAndEquipment')
        },
        children: [
          {
            path: 'instrumentEquipmentLedger',
            name: 'InstrumentEquipmentLedger',
            component: () =>
              import('@/views/ResourceManagement/InstrumentsAndEquipment/InstrumentEquipmentLedger/index.vue'),
            meta: {
              title: t('router.instrumentEquipmentLedger')
            }
          },
          {
            path: 'instrumentModel',
            name: 'InstrumentModel',
            component: () =>
              import('@/views/ResourceManagement/InstrumentsAndEquipment/InstrumentModel/index.vue'),
            meta: {
              title: t('detectionSettings.instrumentModel')
            }
          },
          {
            path: 'instrumentType',
            name: 'InstrumentType',
            component: () =>
              import('@/views/ResourceManagement/InstrumentsAndEquipment/InstrumentType/index.vue'),
            meta: {
              title: t('detectionSettings.instrumentType')
            }
          }
        ]
      }
    ]
  },
  {
    path: '/authorization',
    component: Layout,
    redirect: '/authorization/user',
    name: 'Authorization',
    meta: {
      title: t('router.systemManagement'),
      icon: 'svg-icon:settings',
      alwaysShow: true
    },
    children: [
      {
        path: 'user',
        component: () => import('@/views/Authorization/User/index.vue'),
        name: 'User',
        meta: {
          title: t('router.user')
        }
      },
      {
        path: 'role',
        component: () => import('@/views/Authorization/Role/index.vue'),
        name: 'Role',
        meta: {
          title: t('router.role')
        }
      },
      {
        path: 'menu',
        component: () => import('@/views/Authorization/Menu/index.vue'),
        name: 'Menu',
        meta: {
          title: t('router.menuManagement')
        }
      },
      {
        path: 'department',
        component: () => import('@/views/Authorization/Department/index.vue'),
        name: 'Department',
        meta: {
          title: t('router.department')
        }
      },
      {
        path: 'jobManagement',
        component: () => import('@/views/Authorization/JobManagement/index.vue'),
        name: 'JobManagement',
        meta: {
          title: t('router.jobManagement')
        }
      },
      {
        path: 'dictionaryManagement',
        component: () => import('@/views/Authorization/DictionaryManagement/index.vue'),
        name: 'DictionaryManagement',
        meta: {
          title: t('router.dictionaryManagement')
        }
      },
      {
        path: 'parameterSettings',
        component: () => import('@/views/Authorization/ParameterSettings/index.vue'),
        name: 'ParameterSettings',
        meta: {
          title: t('router.parameterSettings')
        }
      },
      {
        path: 'notificationAnnouncement',
        component: () => import('@/views/Authorization/NotificationAnnouncement/index.vue'),
        name: 'NotificationAnnouncement',
        meta: {
          title: t('router.notificationAnnouncement')
        }
      },
      {
        path: 'logManagement',
        name: 'LogManagement',
        component: getParentLayout(),
        redirect: '/authorization/logManagement/operationLog',
        meta: {
          title: t('router.logManagement')
        },
        children: [
          {
            path: 'operationLog',
            name: 'OperationLog',
            component: () => import('@/views/Authorization/LogManagement/OperationLog/index.vue'),
            meta: {
              title: t('router.operationLog')
            }
          },
          {
            path: 'loginLog',
            name: 'LoginLog',
            component: () => import('@/views/Authorization/LogManagement/LoginLog/index.vue'),
            meta: {
              title: t('router.loginLog')
            }
          }
        ]
      }
    ]
  },
  {
    path: '/adminProcessPermission',
    component: Layout,
    redirect: '/adminProcessPermission/adminSystemAndRole',
    name: 'AdminProcessPermission',
    meta: {
      title: t('router.processPermissionSettings'),
      icon: 'ant-design:dashboard-filled'
    },
    children: [
      {
        path: 'adminSystemAndRole',
        component: () => import('@/views/AdminProcessPermission/AdminSystemAndRole/index.vue'),
        name: 'AdminSystemAndRole',
        meta: {
          title: t('router.processClassification'),
          icon: 'mdi:rhombus'
        }
      },
      {
        path: 'adminProcessForm',
        component: () => import('@/views/AdminProcessPermission/AdminProcessForm/index.vue'),
        name: 'AdminProcessForm',
        meta: {
          title: t('router.processForm'),
          icon: 'mdi:rhombus'
        }
      },
      {
        path: 'adminProcessMonitor',
        component: () => import('@/views/AdminProcessPermission/AdminProcessMonitor/index.vue'),
        name: 'AdminProcessMonitor',
        meta: {
          title: t('router.processMonitor'),
          icon: 'mdi:rhombus'
        }
      },
      {
        path: 'adminProcessExpression',
        component: () => import('@/views/AdminProcessPermission/AdminProcessExpression/index.vue'),
        name: 'AdminProcessExpression',
        meta: {
          title: t('router.processExpression'),
          icon: 'mdi:rhombus'
        }
      },
      {
        path: 'adminProcessInstance',
        component: () => import('@/views/AdminProcessPermission/AdminProcessInstance/index.vue'),
        name: 'AdminProcessInstance',
        meta: {
          title: t('router.processInstance'),
          icon: 'mdi:rhombus'
        }
      },
      {
        path: 'adminProcessTask',
        component: () => import('@/views/AdminProcessPermission/AdminProcessTask/index.vue'),
        name: 'AdminProcessTask',
        meta: {
          title: t('router.processTask'),
          icon: 'mdi:rhombus'
        }
      },
      {
        path: 'bpmProcessInstanceCreate',
        component: () => import('@/views/AdminProcessPermission/AdminInitiateProcess/index.vue'),
        name: 'BpmProcessInstanceCreate',
        meta: {
          title: t('router.initiateProcess'),
          icon: 'mdi:rhombus'
        }
      },
      {
        path: 'bpmProcessInstanceMy',
        component: () => import('@/views/AdminProcessPermission/AdminMyProcess/index.vue'),
        name: 'BpmProcessInstanceMy',
        meta: {
          title: t('router.myProcess'),
          icon: 'mdi:rhombus'
        }
      },
      {
        path: 'bpmProcessInstanceDetail',
        component: () =>
          import('@/views/AdminProcessPermission/AdminInitiateProcess/components/detail/index.vue'),
        name: 'BpmProcessInstanceDetail',
        meta: {
          noCache: true,
          hidden: true,
          canTo: true,
          title: t('router.processDetails'),
          activeMenu: '/adminProcessPermission/adminMyProcess'
        },
        props: (route) => ({
          id: route.query.id,
          taskId: route.query.taskId,
          activityId: route.query.activityId
        })
      },
      {
        path: 'adminToDoTasks',
        component: () => import('@/views/AdminProcessPermission/AdminToDoTasks/index.vue'),
        name: 'AdminToDoTasks',
        meta: {
          title: t('router.toDoTasks'),
          icon: 'mdi:rhombus'
        }
      },
      {
        path: 'adminCompletedTasks',
        component: () => import('@/views/AdminProcessPermission/AdminCompletedTasks/index.vue'),
        name: 'AdminCompletedTasks',
        meta: {
          title: t('router.completedTasks'),
          icon: 'mdi:rhombus'
        }
      },
      {
        path: 'adminCcMe',
        component: () => import('@/views/AdminProcessPermission/AdminCcMe/index.vue'),
        name: 'AdminCcMe',
        meta: {
          title: t('router.ccMe'),
          icon: 'mdi:rhombus'
        }
      }
    ]
  },
  {
    path: '/inspectionMan',
    component: Layout,
    redirect: '/inspectionMan/projectMan/testingPlanMan',
    name: 'InspectionMan',
    meta: {
      title: t('router.detectionManagement'),
      icon: 'svg-icon:report-management'
    },
    children: [
      {
        path: 'projectMan',
        component: getParentLayout(),
        redirect: '/inspectionMan/projectMan/testingPlanMan',
        name: 'ProjectMan',
        meta: {
          title: t('router.projectMan')
        },
        children: [
          {
            path: 'testingPlanMan',
            component: () => import('@/views/InspectionMan/ProjectMan/TestingPlanMan/index.vue'),
            name: 'TestingPlanMan',
            meta: {
              title: t('router.testingPlanMan'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'testingPlanMan/add',
            component: () =>
              import('@/views/InspectionMan/ProjectMan/TestingPlanMan/components/TestingPlanManAdd.vue'),
            name: 'TestingPlanManAdd',
            meta: {
              title: t('inspectionMan.addTestingPlan'),
              activeMenu: '/inspectionMan/testingPlanMan',
              hidden: true,
              canTo: true
            }
          },
          {
            path: 'testingPlanMan/review',
            component: () =>
              import('@/views/InspectionMan/ProjectMan/TestingPlanMan/Review/index.vue'),
            name: 'TestingPlanManReview',
            meta: {
              title: t('inspectionMan.testingPlanReview'),
              activeMenu: '/inspectionMan/testingPlanMan',
              hidden: true,
              canTo: true
            }
          },
          {
            path: 'testingTaskMan',
            component: () => import('@/views/InspectionMan/ProjectMan/TestingTaskMan/index.vue'),
            name: 'TestingTaskMan',
            meta: {
              title: t('router.detectionTaskMan'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'testingTaskMan/issueTasks',
            component: () =>
              import('@/views/InspectionMan/ProjectMan/TestingTaskMan/IssueTasks/index.vue'),
            name: 'TestingTaskManIssueTasks',
            meta: {
              title: t('inspectionManagement.distributeTasks'),
              activeMenu: '/inspectionMan/testingTaskMan',
              hidden: true,
              canTo: true
            }
          },
          {
            path: 'subcontractingTaskMan',
            component: () =>
              import('@/views/InspectionMan/ProjectMan/SubcontractingTaskMan/index.vue'),
            name: 'SubcontractingTaskMan',
            meta: {
              title: t('router.subcontractingTaskMan'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'testingContractMan',
            component: () =>
              import('@/views/InspectionMan/ProjectMan/TestingContractMan/index.vue'),
            name: 'TestingContractMan',
            meta: {
              title: t('router.testingContractMan'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'testingContractMan/create',
            component: () =>
              import('@/views/InspectionMan/ProjectMan/TestingContractMan/Create/index.vue'),
            name: 'TestingContractManCreate',
            meta: {
              title: t('inspectionMan.createContract'),
              activeMenu: '/inspectionMan/testingContractMan',
              hidden: true,
              canTo: true
            }
          }
        ]
      },
      {
        path: 'samplingTask',
        component: getParentLayout(),
        redirect: '/inspectionMan/samplingTask/samplingTaskAllocation',
        name: 'SamplingTask',
        meta: {
          title: t('router.samplingTask')
        },
        children: [
          {
            path: 'samplingTaskAllocation',
            component: () =>
              import('@/views/InspectionMan/SamplingTask/SamplingTaskAllocation/index.vue'),
            name: 'SamplingTaskAllocation',
            meta: {
              title: t('router.samplingTaskAllocation'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'samplingTaskAllocation/assign',
            component: () =>
              import('@/views/InspectionMan/SamplingTask/SamplingTaskAllocation/Assign/index.vue'),
            name: 'SamplingTaskAllocationAssign',
            meta: {
              title: t('router.assignSamplingTasks'),
              activeMenu: '/inspectionMan/samplingTask/samplingTaskAllocation',
              hidden: true,
              canTo: true
            }
          },
          {
            path: 'samplingTaskAllocation/config',
            component: () =>
              import('@/views/InspectionMan/SamplingTask/SamplingTaskAllocation/Config/index.vue'),
            name: 'SamplingTaskAllocationConfig',
            meta: {
              title: t('router.samplingConfig'),
              activeMenu: '/inspectionMan/samplingTask/samplingTaskAllocation',
              hidden: true,
              canTo: true
            }
          },
          {
            path: 'samplingTaskReception',
            component: () =>
              import('@/views/InspectionMan/SamplingTask/SamplingTaskReception/index.vue'),
            name: 'SamplingTaskReception',
            meta: {
              title: t('router.samplingTaskReception'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'samplingRecordGeneration',
            component: () =>
              import('@/views/InspectionMan/SamplingTask/SamplingRecordGeneration/index.vue'),
            name: 'SamplingRecordGeneration',
            meta: {
              title: t('router.samplingRecordGeneration'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'samplingRecordFillIn',
            component: () =>
              import('@/views/InspectionMan/SamplingTask/SamplingRecordFillIn/index.vue'),
            name: 'SamplingRecordFillIn',
            meta: {
              title: t('router.samplingRecordFillIn'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'samplingRecordReview',
            component: () =>
              import('@/views/InspectionMan/SamplingTask/SamplingRecordReview/index.vue'),
            name: 'SamplingRecordReview',
            meta: {
              title: t('router.samplingRecordReview'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'samplingRecordVerification',
            component: () =>
              import('@/views/InspectionMan/SamplingTask/SamplingRecordVerification/index.vue'),
            name: 'SamplingRecordVerification',
            meta: {
              title: t('router.samplingRecordVerification'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'samplingRecordModification',
            component: () =>
              import('@/views/InspectionMan/SamplingTask/SamplingRecordModification/index.vue'),
            name: 'SamplingRecordModification',
            meta: {
              title: t('router.samplingRecordModification'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'samplingRecordOverview',
            component: () =>
              import('@/views/InspectionMan/SamplingTask/SamplingRecordOverview/index.vue'),
            name: 'SamplingRecordOverview',
            meta: {
              title: t('router.samplingRecordOverview'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'samplingRecordOverview/config',
            component: () =>
              import('@/views/InspectionMan/SamplingTask/SamplingRecordOverview/Config/index.vue'),
            name: 'SamplingRecordOverviewConfig',
            meta: {
              title: t('router.recordOverViewDetail'),
              activeMenu: '/inspectionMan/samplingTask/samplingRecordOverview',
              hidden: true,
              canTo: true
            }
          }
        ]
      },
      {
        path: 'onSiteInspection',
        component: getParentLayout(),
        redirect: '/inspectionMan/onSiteInspection/fieldRecordGeneration',
        name: 'OnSiteInspection',
        meta: {
          title: t('router.onSiteInspection')
        },
        children: [
          {
            path: 'fieldRecordGeneration',
            component: () =>
              import('@/views/InspectionMan/OnSiteInspection/FieldRecordGeneration/index.vue'),
            name: 'FieldRecordGeneration',
            meta: {
              title: t('router.fieldRecordGeneration'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'onSiteRecordFillIn',
            component: () =>
              import('@/views/InspectionMan/OnSiteInspection/OnSiteRecordFillIn/index.vue'),
            name: 'OnSiteRecordFillIn',
            meta: {
              title: t('router.onSiteRecordFillIn'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'onSiteRecordReview',
            component: () =>
              import('@/views/InspectionMan/OnSiteInspection/OnSiteRecordReview/index.vue'),
            name: 'OnSiteRecordReview',
            meta: {
              title: t('router.onSiteRecordReview'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'onSiteRecordVerification',
            component: () =>
              import('@/views/InspectionMan/OnSiteInspection/OnSiteRecordVerification/index.vue'),
            name: 'OnSiteRecordVerification',
            meta: {
              title: t('router.onSiteRecordVerification'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'onSiteRecordModification',
            component: () =>
              import('@/views/InspectionMan/OnSiteInspection/OnSiteRecordModification/index.vue'),
            name: 'OnSiteRecordModification',
            meta: {
              title: t('router.onSiteRecordModification'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'onSiteRecordLedger',
            component: () =>
              import('@/views/InspectionMan/OnSiteInspection/OnSiteRecordLedger/index.vue'),
            name: 'OnSiteRecordLedger',
            meta: {
              title: t('router.onSiteRecordLedger'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'onSiteRecordLedger/config',
            component: () =>
              import('@/views/InspectionMan/OnSiteInspection/OnSiteRecordLedger/Config/index.vue'),
            name: 'OnSiteRecordLedgerConfig',
            meta: {
              title: t('router.onSiteTaskDetail'),
              activeMenu: '/inspectionMan/onSiteInspection/onSiteRecordLedger',
              hidden: true,
              canTo: true
            }
          }
        ]
      },
      {
        path: 'sampleManagement',
        component: getParentLayout(),
        redirect: '/inspectionMan/sampleManagement/submitSample',
        name: 'SampleManagement',
        meta: {
          title: t('router.sampleManagement')
        },
        children: [
          {
            path: 'submitSample',
            component: () =>
              import('@/views/InspectionMan/SampleManagement/SubmitSample/index.vue'),
            name: 'SubmitSample',
            meta: {
              title: t('router.submitSample'),
              icon: 'svg-icon:detection-management'
            }
          },
          // {
          //   path: 'receiveSample',
          //   component: () =>
          //     import('@/views/InspectionMan/SampleManagement/ReceiveSample/index.vue'),
          //   name: 'ReceiveSample',
          //   meta: {
          //     title: t('router.sampleReception'),
          //     icon: 'svg-icon:detection-management'
          //   }
          // },
          {
            path: 'sampleQualityControl',
            component: () =>
              import('@/views/InspectionMan/SampleManagement/SampleQualityControl/index.vue'),
            name: 'SampleQualityControl',
            meta: {
              title: t('router.sampleQualityControl'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'sampleAllocation',
            component: () =>
              import('@/views/InspectionMan/SampleManagement/SampleAllocation/index.vue'),
            name: 'SampleAllocation',
            meta: {
              title: t('router.sampleAllocation'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'sampleCirculation',
            component: () =>
              import('@/views/InspectionMan/SampleManagement/SampleCirculation/index.vue'),
            name: 'SampleCirculation',
            meta: {
              title: t('router.sampleCirculation'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'sampleRetention',
            component: () =>
              import('@/views/InspectionMan/SampleManagement/SampleRetention/index.vue'),
            name: 'SampleRetention',
            meta: {
              title: t('router.sampleRetention'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'sampleDisposal',
            component: () =>
              import('@/views/InspectionMan/SampleManagement/SampleDisposal/index.vue'),
            name: 'SampleDisposal',
            meta: {
              title: t('contractMan.sampleDisposal'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'sampleLedger',
            component: () =>
              import('@/views/InspectionMan/SampleManagement/SampleLedger/index.vue'),
            name: 'SampleLedger',
            meta: {
              title: t('router.sampleLedger'),
              icon: 'svg-icon:detection-management'
            }
          }
        ]
      },
      {
        path: 'analysisManagement',
        component: getParentLayout(),
        redirect: '/inspectionMan/analysisManagement/analysisTaskReception',
        name: 'AnalysisManagement',
        meta: {
          title: t('router.analysisManagement')
        },
        children: [
          {
            path: 'analysisTaskReception',
            component: () =>
              import('@/views/InspectionMan/AnalysisManagement/AnalysisTaskReception/index.vue'),
            name: 'AnalysisTaskReception',
            meta: {
              title: t('router.analysisTaskReception'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'analysisRecordGeneration',
            component: () =>
              import('@/views/InspectionMan/AnalysisManagement/AnalysisRecordGeneration/index.vue'),
            name: 'AnalysisRecordGeneration',
            meta: {
              title: t('router.analysisRecordGeneration'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'analysisRecordReview',
            component: () =>
              import('@/views/InspectionMan/AnalysisManagement/AnalysisRecordReview/index.vue'),
            name: 'AnalysisRecordReview',
            meta: {
              title: t('router.analysisRecordReview'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'analysisRecordApproval',
            component: () =>
              import('@/views/InspectionMan/AnalysisManagement/AnalysisRecordApproval/index.vue'),
            name: 'AnalysisRecordApproval',
            meta: {
              title: t('router.analysisRecordApproval'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'analysisRecordModification',
            component: () =>
              import('@/views/InspectionMan/AnalysisManagement/AnalysisRecordModification/index.vue'),
            name: 'AnalysisRecordModification',
            meta: {
              title: t('router.analysisRecordModification'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'analysisTaskLedger',
            component: () =>
              import('@/views/InspectionMan/AnalysisManagement/AnalysisTaskLedger/index.vue'),
            name: 'AnalysisTaskLedger',
            meta: {
              title: t('router.analysisTaskLedger'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'analysisTaskLedger/config',
            component: () =>
              import('@/views/InspectionMan/AnalysisManagement/AnalysisTaskLedger/Config/index.vue'),
            name: 'AnalysisTaskLedgerConfig',
            meta: {
              title: t('router.analyzeRecordDetails'),
              activeMenu: '/inspectionMan/analysisManagement/analysisTaskLedger',
              hidden: true
            }
          }
        ]
      },
      {
        path: 'reportManagement',
        component: getParentLayout(),
        redirect: '/inspectionMan/reportManagement/reportGeneration',
        name: 'ReportManagement',
        meta: {
          title: t('router.reportManagement')
        },
        children: [
          {
            path: 'reportGeneration',
            component: () =>
              import('@/views/InspectionMan/ReportManagement/ReportGeneration/index.vue'),
            name: 'ReportGeneration',
            meta: {
              title: t('router.reportGeneration'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'reportPreparation',
            component: () =>
              import('@/views/InspectionMan/ReportManagement/ReportPreparation/index.vue'),
            name: 'ReportPreparation',
            meta: {
              title: t('router.reportPreparation'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'reportReview',
            component: () =>
              import('@/views/InspectionMan/ReportManagement/ReportReview/index.vue'),
            name: 'ReportReview',
            meta: {
              title: t('router.reportReview'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'reportIssuance',
            component: () =>
              import('@/views/InspectionMan/ReportManagement/ReportIssuance/index.vue'),
            name: 'ReportIssuance',
            meta: {
              title: t('router.reportIssuance'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'reportArchiving',
            component: () =>
              import('@/views/InspectionMan/ReportManagement/ReportArchiving/index.vue'),
            name: 'ReportArchiving',
            meta: {
              title: t('router.reportArchiving'),
              icon: 'svg-icon:detection-management'
            }
          }
        ]
      }
    ]
  },
  {
    path: '/standardMan',
    component: Layout,
    redirect: '/standardMan/projectCompleteView',
    name: 'StandardMan',
    meta: {
      title: t('router.standardManagement'),
      icon: 'svg-icon:report-management'
    },
    children: [
      {
        path: 'projectCompleteView',
        component: () => import('@/views/StandardMan/ProjectCompleteView/index.vue'),
        name: 'ProjectCompleteView',
        meta: {
          title: t('router.projectCompleteView'),
          icon: 'svg-icon:detection-management'
        }
      },
      {
        path: 'projectType',
        component: () => import('@/views/StandardMan/ProjectType/index.vue'),
        name: 'ProjectType',
        meta: {
          title: t('router.projectType'),
          icon: 'svg-icon:detection-management'
        }
      },
      {
        path: 'testingItems',
        component: () => import('@/views/StandardMan/TestingItems/index.vue'),
        name: 'TestingItems',
        meta: {
          title: t('sampleCirculation.testingItems'),
          icon: 'svg-icon:detection-management'
        }
      },
      {
        path: 'samplingGroupingRules',
        component: () => import('@/views/StandardMan/SamplingGroupingRules/index.vue'),
        name: 'SamplingGroupingRules',
        meta: {
          title: t('router.samplingGroupingRules'),
          icon: 'svg-icon:detection-management'
        }
      },
      {
        path: 'samplingStandards',
        component: () => import('@/views/StandardMan/SamplingStandards/index.vue'),
        name: 'SamplingStandards',
        meta: {
          title: t('inspectionMan.samplingStandards'),
          icon: 'svg-icon:detection-management'
        }
      },
      {
        path: 'samplingMethod',
        component: () => import('@/views/StandardMan/SamplingMethod/index.vue'),
        name: 'SamplingMethod',
        meta: {
          title: t('sampleReception.samplingMethod'),
          icon: 'svg-icon:detection-management'
        }
      },
      {
        path: 'testingStandard',
        component: () => import('@/views/StandardMan/TestingStandard/index.vue'),
        name: 'TestingStandard',
        meta: {
          title: t('contractMan.testingStandard'),
          icon: 'svg-icon:detection-management'
        }
      },
      {
        path: 'testMethod',
        component: () => import('@/views/StandardMan/TestMethod/index.vue'),
        name: 'TestMethod',
        meta: {
          title: t('detectionManagement.testMethod'),
          icon: 'svg-icon:detection-management'
        }
      },
      {
        path: 'evaluationCriteria',
        component: () => import('@/views/StandardMan/EvaluationCriteria/index.vue'),
        name: 'EvaluationCriteria',
        meta: {
          title: t('inspectionMan.evaluationCriteria'),
          icon: 'svg-icon:detection-management'
        }
      }
    ]
  },
  {
    path: '/businessConfig',
    component: Layout,
    redirect: '/businessConfig/templateMan/samplingTemplate',
    name: 'BusinessConfig',
    meta: {
      title: t('router.businessConfig'),
      icon: 'svg-icon:report-management'
    },
    children: [
      {
        path: 'templateMan',
        component: getParentLayout(),
        redirect: '/businessConfig/templateMan/samplingTemplate',
        name: 'TemplateMan',
        meta: {
          title: t('router.templateMan')
        },
        children: [
          {
            path: 'samplingTemplate',
            component: () =>
              import('@/views/BusinessConfig/TemplateMan/SamplingTemplate/index.vue'),
            name: 'SamplingTemplate',
            meta: {
              title: t('router.samplingTemplate'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'onSiteInspectionTemplate',
            component: () =>
              import('@/views/BusinessConfig/TemplateMan/OnSiteInspectionTemplate/index.vue'),
            name: 'OnSiteInspectionTemplate',
            meta: {
              title: t('router.onSiteInspectionTemplate'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'analysisTemplate',
            component: () =>
              import('@/views/BusinessConfig/TemplateMan/AnalysisTemplate/index.vue'),
            name: 'AnalysisTemplate',
            meta: {
              title: t('router.analysisTemplate'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'reportTemplate',
            component: () => import('@/views/BusinessConfig/TemplateMan/ReportTemplate/index.vue'),
            name: 'ReportTemplate',
            meta: {
              title: t('router.reportTemplate'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'reportTemplateHG',
            component: () =>
              import('@/views/BusinessConfig/TemplateMan/ReportTemplateHG/index.vue'),
            name: 'ReportTemplateHG',
            meta: {
              title: `${t('router.reportTemplate')}HG`,
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'statementTemplate',
            component: () =>
              import('@/views/BusinessConfig/TemplateMan/StatementTemplate/index.vue'),
            name: 'StatementTemplate',
            meta: {
              title: t('router.statementTemplate'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'generalForm',
            component: () => import('@/views/BusinessConfig/TemplateMan/GeneralForm/index.vue'),
            name: 'GeneralForm',
            meta: {
              title: t('router.generalForm'),
              icon: 'svg-icon:detection-management'
            }
          }
        ]
      },
      {
        path: 'processMan',
        component: getParentLayout(),
        redirect: '/businessConfig/processMan/processManCopy',
        name: 'ProcessMan',
        meta: {
          title: t('router.processMan')
        },
        children: [
          {
            path: 'processManCopy',
            component: () => import('@/views/BusinessConfig/ProcessMan/ProcessManCopy/index.vue'),
            name: 'ProcessManCopy',
            meta: {
              title: t('router.processMan'),
              icon: 'svg-icon:detection-management'
            }
          }
        ]
      },
      {
        path: 'generalConfig',
        component: getParentLayout(),
        redirect: '/businessConfig/generalConfig/personalizedConfig',
        name: 'GeneralConfig',
        meta: {
          title: t('router.generalConfig')
        },
        children: [
          {
            path: 'personalizedConfig',
            component: () =>
              import('@/views/BusinessConfig/GeneralConfig/PersonalizedConfig/index.vue'),
            name: 'PersonalizedConfig',
            meta: {
              title: t('router.personalizedConfig'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'systemStyleConfig',
            component: () =>
              import('@/views/BusinessConfig/GeneralConfig/SystemStyleConfig/index.vue'),
            name: 'SystemStyleConfig',
            meta: {
              title: t('router.systemStyleConfig'),
              icon: 'svg-icon:detection-management'
            }
          }
        ]
      }
    ]
  },
  {
    path: '/personnelMan',
    component: Layout,
    redirect: '/personnelMan/personnelManagement/personnelList',
    name: 'PersonnelMan',
    meta: {
      title: t('router.personnelMan'),
      icon: 'svg-icon:report-management'
    },
    children: [
      {
        path: 'personnelManagement',
        component: getParentLayout(),
        redirect: '/personnelMan/personnelManagement/personnelList',
        name: 'PersonnelManagement',
        meta: {
          title: t('router.personnelManagement')
        },
        children: [
          {
            path: 'personnelList',
            component: () =>
              import('@/views/PersonnelMan/PersonnelManagement/PersonnelList/index.vue'),
            name: 'PersonnelList',
            meta: {
              title: t('router.personnelList'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'personnelSignature',
            component: () =>
              import('@/views/PersonnelMan/PersonnelManagement/PersonnelSignature/index.vue'),
            name: 'PersonnelSignature',
            meta: {
              title: t('router.personnelSignature'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'scopeCapabilities',
            component: () =>
              import('@/views/PersonnelMan/PersonnelManagement/ScopeCapabilities/index.vue'),
            name: 'ScopeCapabilities',
            meta: {
              title: t('router.scopeCapabilities'),
              icon: 'svg-icon:detection-management'
            }
          }
        ]
      }
    ]
  },
  {
    path: '/businessMan',
    component: Layout,
    redirect: '/businessMan/subcontractor/subcontractorMan',
    name: 'BusinessMan',
    meta: {
      title: t('router.businessMan'),
      icon: 'svg-icon:report-management'
    },
    children: [
      {
        path: 'subcontractor',
        component: getParentLayout(),
        redirect: '/businessMan/subcontractor/subcontractorMan',
        name: 'Subcontractor',
        meta: {
          title: t('inspectionMan.subcontractor')
        },
        children: [
          {
            path: 'subcontractorMan',
            component: () => import('@/views/BusinessMan/Subcontractor/SubcontractorMan/index.vue'),
            name: 'SubcontractorMan',
            meta: {
              title: t('router.subcontractorMan'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'scopeSubCapability',
            component: () =>
              import('@/views/BusinessMan/Subcontractor/ScopeSubCapability/index.vue'),
            name: 'ScopeSubCapability',
            meta: {
              title: t('router.scopeSubCapability'),
              icon: 'svg-icon:detection-management'
            }
          }
        ]
      },
      {
        path: 'clientManagement',
        component: () => import('@/views/BusinessMan/ClientManagement/index.vue'),
        name: 'ClientManagement',
        meta: {
          title: t('router.clientManagement')
        }
      }
    ]
  },
  {
    path: '/inspectionManHG',
    component: Layout,
    redirect: '/inspectionManHG/entrustedMan/inspectionApplicationFormMan',
    name: 'InspectionManHG',
    meta: {
      title: `${t('router.detectionManagement')}(HG)`,
      icon: 'svg-icon:report-management'
    },
    children: [
      {
        path: 'entrustedMan',
        component: getParentLayout(),
        redirect: '/inspectionManHG/entrustedMan/inspectionApplicationFormMan',
        name: 'EntrustedMan',
        meta: {
          title: t('router.entrustedMan')
        },
        children: [
          {
            path: 'inspectionApplicationFormMan',
            component: () =>
              import('@/views/InspectionManHG/EntrustedMan/InspectionApplicationFormMan/index.vue'),
            name: 'InspectionApplicationFormMan',
            meta: {
              title: t('router.inspectionApplicationFormMan'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'billingInvoicing',
            component: () =>
              import('@/views/InspectionManHG/EntrustedMan/BillingInvoicing/index.vue'),
            name: 'BillingInvoicing',
            meta: { title: t('router.billingInvoicing'), icon: 'svg-icon:detection-management' }
          },
          {
            path: 'agreementManagement',
            component: () =>
              import('@/views/InspectionManHG/EntrustedMan/AgreementManagement/index.vue'),
            name: 'AgreementManagement',
            meta: {
              title: t('inspectionManHG.agreementManagement'),
              icon: 'svg-icon:detection-management'
            }
          }
        ]
      },
      {
        path: '/sampleManagementHG',
        component: getParentLayout(),
        redirect: '/inspectionManHG/sampleManagementHG/sampleCirculation',
        name: 'SampleManagementHG',
        meta: {
          title: t('router.sampleManagement'),
          icon: 'svg-icon:sample-management'
        },
        children: [
          {
            path: 'sampleCirculation',
            component: () =>
              import('@/views/InspectionManHG/SampleManagement/SampleCirculation/index.vue'),
            name: 'SampleCirculationHG',
            meta: {
              title: t('router.sampleCirculation'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'sampleRetentionDisposal',
            component: () =>
              import('@/views/InspectionManHG/SampleManagement/SampleRetentionDisposal/index.vue'),
            name: 'SampleRetentionDisposalHG',
            meta: {
              title: t('router.sampleRetentionDisposal'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'sampleLedger',
            component: () =>
              import('@/views/InspectionManHG/SampleManagement/SampleLedger/index.vue'),
            name: 'SampleLedgerHG',
            meta: {
              title: t('router.sampleLedger'),
              icon: 'svg-icon:detection-management'
            }
          }
        ]
      },
      {
        path: 'detectionTask',
        component: getParentLayout(),
        redirect: '/inspectionManHG/detectionTask/allocationClaim',
        name: 'DetectionTask',
        meta: {
          title: t('detectionManagement.detectionTask')
        },
        children: [
          {
            path: 'allocationClaim',
            component: () =>
              import('@/views/InspectionManHG/DetectionTask/AllocationClaim/index.vue'),
            name: 'AllocationClaim',
            meta: {
              title: t('router.allocationClaim'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'generateInspectionRecords',
            component: () =>
              import('@/views/InspectionManHG/DetectionTask/GenerateInspectionRecords/index.vue'),
            name: 'GenerateInspectionRecords',
            meta: {
              title: t('router.generateInspectionRecords'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'fillResults',
            component: () => import('@/views/InspectionManHG/DetectionTask/FillResults/index.vue'),
            name: 'FillResults',
            meta: {
              title: t('router.fillResults'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'resultReviews',
            component: () =>
              import('@/views/InspectionManHG/DetectionTask/ResultReviews/index.vue'),
            name: 'ResultReviews',
            meta: {
              title: t('router.resultReviews'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'resultReview',
            component: () => import('@/views/InspectionManHG/DetectionTask/ResultReview/index.vue'),
            name: 'ResultReview',
            meta: {
              title: t('router.resultReview'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            // 结果修改
            path: 'resultModify',
            component: () => import('@/views/InspectionManHG/DetectionTask/ResultModify/index.vue'),
            name: 'ResultModify',
            meta: {
              title: t('router.resultModify'),
              icon: 'svg-icon:detection-management',
              hidden: true
            }
          },
          {
            path: 'inspectionRecordLedger',
            component: () =>
              import('@/views/InspectionManHG/DetectionTask/InspectionRecordLedger/index.vue'),
            name: 'InspectionRecordLedger',
            meta: {
              title: t('router.inspectionRecordLedger'),
              icon: 'svg-icon:detection-management'
            }
          }
        ]
      },
      {
        path: 'reportManagementHG',
        component: getParentLayout(),
        name: 'ReportManagementHG',
        redirect: '/inspectionManHG/reportManagementHG/reportGenerationHG',
        meta: {
          title: t('router.reportManagement'),
          icon: 'svg-icon:detection-management'
        },
        children: [
          {
            path: 'reportGenerationHG',
            component: () =>
              import('@/views/InspectionManHG/ReportManagementHG/ReportGenerationHG/index.vue'),
            name: 'ReportGenerationHG',
            meta: {
              title: t('router.reportGeneration'),
              icon: 'svg-icon:detection-management'
            }
          },
          // 报告编制，报告审核，报告签发，报告归档
          {
            path: 'reportCompilationHG',
            component: () =>
              import('@/views/InspectionManHG/ReportManagementHG/ReportCompilationHG/index.vue'),
            name: 'ReportCompilationHG',
            meta: {
              title: t('router.reportPreparation'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'reportAuditHG',
            component: () =>
              import('@/views/InspectionManHG/ReportManagementHG/ReportAuditHG/index.vue'),
            name: 'ReportAuditHG',
            meta: {
              title: t('router.reportReview'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'reportIssueHG',
            component: () =>
              import('@/views/InspectionManHG/ReportManagementHG/ReportIssueHG/index.vue'),
            name: 'ReportIssueHG',
            meta: {
              title: t('router.reportIssuance'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'reportArchiveHG',
            component: () =>
              import('@/views/InspectionManHG/ReportManagementHG/ReportArchiveHG/index.vue'),
            name: 'ReportArchiveHG',
            meta: {
              title: t('router.reportArchiving'),
              icon: 'svg-icon:detection-management'
            }
          },
          {
            path: 'reportLedgerHG',
            component: () =>
              import('@/views/InspectionManHG/ReportManagementHG/ReportLedgerHG/index.vue'),
            name: 'ReportLedgerHG',
            meta: {
              title: t('router.reportLedger'),
              icon: 'svg-icon:detection-management'
            }
          }
        ]
      }
    ]
  }
]

const router = createRouter({
  // createWebHashHistory
  history: createWebHistory('/'),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
